import { useModal } from "@ebay/nice-modal-react";
import { useCallback } from "react";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";

import FormFieldSpace from "components/FormFields/Space";
import FormFieldTags from "components/FormFields/Tags";
import Button from "ds/components/Button";
import DrawerBody from "ds/components/Drawer/Body";
import DrawerFooter from "ds/components/Drawer/Footer";
import DrawerFooterActions from "ds/components/Drawer/FooterActions";
import DrawerHeader from "ds/components/Drawer/Header";
import DrawerSection from "ds/components/Drawer/Section";
import DrawerCancelButton from "ds/components/DrawerNew/CancelButton";
import DrawerCloseIcon from "ds/components/DrawerNew/CloseIcon";
import DrawerForm from "ds/components/DrawerNew/Form";
import DrawerHeaderTitle from "ds/components/DrawerNew/HeaderTitle";
import { createDrawer, createDrawerTrigger } from "ds/components/DrawerNew/utils";
import FormField from "ds/components/Form/Field";
import Input from "ds/components/Input";
import Textarea from "ds/components/Textarea";
import useUpdateSpaceliftVcsRepo from "shared/SpaceliftVcsRepo/useUpdateSpaceliftVcsRepo";
import { Repo } from "types/generated";
import { getDrawerFormFix } from "utils/css";
import { stringIsRequired } from "utils/formValidators";

type EditSpaceliftVcsRepoFormFields = {
  name: string;
  description: string;
  space: string;
  labels: { value: string }[];
};

type EditSpaceliftVcsRepoDrawerProps = {
  repo: Repo;
};

const EditSpaceliftVcsRepoDrawer = createDrawer(({ repo }: EditSpaceliftVcsRepoDrawerProps) => {
  const drawer = useModal();
  const { updateSpaceliftVcsRepo, loading } = useUpdateSpaceliftVcsRepo();

  const form = useForm<EditSpaceliftVcsRepoFormFields>({
    defaultValues: {
      name: repo.name,
      description: repo.description || "",
      space: repo.space?.id || "",
      labels: repo.labels?.map((l) => ({ value: l })) || [],
    },
    mode: "onChange",
  });

  const {
    register,
    handleSubmit,
    formState: { errors, isDirty, isValid },
  } = form;

  const onSubmit: SubmitHandler<EditSpaceliftVcsRepoFormFields> = useCallback(
    (data) => {
      updateSpaceliftVcsRepo(
        repo.id,
        {
          name: data.name,
          description: data.description || null,
          labels: data.labels.length > 0 ? data.labels.map((l) => l.value) : null,
          isDefault: null,
          vcsCheckOptions: null,
          vcsChecks: null,
        },
        () => drawer.hide()
      );
    },
    [updateSpaceliftVcsRepo, drawer, repo.id]
  );

  return (
    <DrawerForm isDirty={isDirty}>
      <FormProvider {...form}>
        <form onSubmit={handleSubmit(onSubmit)} {...getDrawerFormFix()}>
          <DrawerHeader justify="between">
            <DrawerHeaderTitle title="Edit repo" />
            <DrawerCloseIcon />
          </DrawerHeader>
          <DrawerBody fullHeight noPadding>
            <DrawerSection gap="x-large">
              <FormField label="Name" error={errors?.name?.message} noMargin>
                {({ ariaInputProps }) => (
                  <Input
                    placeholder="Enter repo name"
                    error={!!errors?.name}
                    {...register("name", {
                      validate: stringIsRequired(),
                    })}
                    {...ariaInputProps}
                  />
                )}
              </FormField>

              <FormField label="Description" error={errors?.description?.message} noMargin>
                {({ ariaInputProps }) => (
                  <Textarea
                    placeholder="Enter description (optional)"
                    rows={4}
                    {...register("description")}
                    {...ariaInputProps}
                  />
                )}
              </FormField>

              {repo.space && (
                <FormFieldSpace noMargin spaces={[repo.space]} loading={false} disabled />
              )}

              <FormFieldTags label="Labels" tagName="label" name="labels" isOptional noMargin />
            </DrawerSection>
          </DrawerBody>
          <DrawerFooter>
            <DrawerFooterActions>
              <DrawerCancelButton />
              <Button
                type="submit"
                variant="primary"
                loading={loading}
                disabled={!isDirty || !isValid}
              >
                Save
              </Button>
            </DrawerFooterActions>
          </DrawerFooter>
        </form>
      </FormProvider>
    </DrawerForm>
  );
});

export const showEditSpaceliftVcsRepoDrawer = createDrawerTrigger(EditSpaceliftVcsRepoDrawer);
